import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent } from './confirm-dialog.component';

export interface ConfirmDialogData {
  message: string;
  title?: string;
  /** Defaults to “OK” */
  confirmText?: string;
  /** If you omit `cancelText`, it becomes an Alert (OK-only) */
  cancelText?: string;
}

@Injectable({ providedIn: 'root' })
export class ConfirmService {
  private dialog = inject(MatDialog);

  /** 
   * Opens as a *confirm* dialog (Cancel + OK).
   * Resolves `true` only if “OK” clicked.
   */
  async confirm(
    message: string | ConfirmDialogData,
    confirmText?: string,
    cancelText = 'Cancel'
  ): Promise<boolean> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: typeof message === 'string'
        ? { message, confirmText, cancelText, title: 'Confirm' } as ConfirmDialogData
        : { cancelText: 'Cancel', ...message } as ConfirmDialogData
    });
    return firstValueFrom(ref.afterClosed()).then(r => r === true);
  }

  /**
   * Opens as an *alert* (OK only).
   * Resolves once “OK” clicked.
   */
  async alert(message: string | ConfirmDialogData, okText = 'OK'): Promise<void> {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: typeof message === 'string'
        ? { message, confirmText: okText } as ConfirmDialogData
        : { ...message, confirmText: okText } as ConfirmDialogData
    });
    return await firstValueFrom(ref.afterClosed());
  }
}
